import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useLingui } from '@lingui/react';
import { msg, t } from '@lingui/macro';
import { Trans } from '@lingui/react/macro';
import { Search, FileText, Check, Loader2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useChildren } from '@/hooks/useChildren';
import { useContracts } from '@/hooks/useBilling';
import { useChildcares } from '@/hooks/settings/useChildcare';
import type { Childcare } from '@/types/organisation';
import { useImageUrl } from '@/hooks/useImage';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { isImageId, isDirectImageUrl } from '@/utils/imageUtils';
import { formatBirthDateWithAge } from '@/utils/formatChildAge';
import { cn } from '@/lib/utils';

interface ChildSelectorProps {
  selectedChildId?: string;
  selectedChildcareId?: string;
  onSelectChild: (childId: string) => void;
  onSelectChildcare: (childcareId: string) => void;
}

// Type for child data that handles both camelCase and snake_case field names
interface ChildData {
  id: string;
  firstname?: string;
  first_name?: string;
  lastname?: string;
  last_name?: string;
  birthdate?: string;
  birth_date?: string;
  avatarUrl?: string;
  childInternalId?: string;
  child_internal_id?: string;
  [key: string]: unknown;
}

// Type for contract data
// Shape AFTER ContractTransformer.arrayToFrontend(), which is what useContracts
// hands back: keys are camelCase, values are untouched (so `status` arrives in
// the API's lower case, e.g. "active"). Declaring the snake_case key here is
// what let the reads below drift out of sync with the payload.
interface ContractData {
  id: string;
  childId?: string;
  status?: string;
  [key: string]: unknown;
}

// Separate component for child card to handle image resolution
interface ChildCardProps {
  child: ChildData;
  isSelected: boolean;
  onSelect: () => void;
  contracts: ContractData[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  i18n: any; // Lingui's i18n type is complex, using any here is acceptable
}

function ChildCard({ child, isSelected, onSelect, contracts, i18n }: ChildCardProps) {
  // Handle both camelCase and snake_case field names
  const birthdate = child.birthdate || child.birth_date;
  const avatarUrlRaw = child.avatarUrl;
  const firstname = child.firstname || child.first_name;
  const lastname = child.lastname || child.last_name;
  const childInternalId = child.childInternalId || child.child_internal_id;
  
  // Handle empty strings by treating them as null/undefined
  const avatarUrl = avatarUrlRaw?.trim() || undefined;
  
  // Check if avatarUrl is an image ID that needs resolution
  const needsImageResolution = isImageId(avatarUrl);
  
  // Use the useImageUrl hook to resolve image ID to URL if needed
  const resolvedImageUrl = useImageUrl(needsImageResolution ? avatarUrl : null);
  
  // Determine the final avatar URL to use
  let finalAvatarUrl: string | undefined;
  if (needsImageResolution) {
    finalAvatarUrl = resolvedImageUrl || undefined;
  } else if (isDirectImageUrl(avatarUrl)) {
    finalAvatarUrl = avatarUrl;
  } else {
    finalAvatarUrl = undefined; // Invalid or empty value
  }
  
  // Normalise before comparing: the API serialises "active", not "ACTIVE".
  // Same shape as AllocationForm / CreateSettlingInDialog.
  const activeContracts = contracts.filter(
    c => (c.status || '').toUpperCase() === 'ACTIVE'
  );
  
  const getInitials = (firstname?: string, lastname?: string) => {
    const firstInitial = firstname?.charAt(0)?.toUpperCase() || '';
    const lastInitial = lastname?.charAt(0)?.toUpperCase() || '';
    return `${firstInitial}${lastInitial}`;
  };
  
  const formatBirthDate = (birthDate: string | undefined) => {
    return formatBirthDateWithAge(birthDate, i18n.locale, i18n);
  };
  
  const initials = getInitials(firstname, lastname);
  
  return (
    <div
      className={cn(
        "cursor-pointer transition-all duration-200 p-2.5 rounded-lg border shadow-sm",
        isSelected
          ? "bg-primary/5 border-primary"
          : "bg-card border-border hover:bg-muted/50 hover:border-muted-foreground/30"
      )}
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      aria-pressed={isSelected}
      aria-label={i18n._(msg`Select ${firstname ?? ''} ${lastname ?? ''}, born ${formatBirthDate(birthdate)}`)}
    >
      <div className="flex items-center gap-3">
        {/* Avatar with initials - keep size at 48px */}
        <Avatar className="h-12 w-12 flex-shrink-0">
          {finalAvatarUrl && (
            <AvatarImage 
              src={finalAvatarUrl} 
              alt={`${firstname} ${lastname}`}
            />
          )}
          <AvatarFallback className="bg-primary/10 text-primary font-semibold">
            {initials}
          </AvatarFallback>
        </Avatar>

        {/* Child info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h4 className="font-medium text-sm leading-tight truncate">
                {firstname} {lastname}{childInternalId && ` (${childInternalId})`}
              </h4>
              <p className="text-xs text-muted-foreground mt-0.5">
                {formatBirthDate(birthdate)}
              </p>
            </div>
            
            {/* Selected indicator */}
            {isSelected && (
              <div className="ml-2 flex-shrink-0">
                <div className="h-5 w-5 rounded-full bg-primary flex items-center justify-center">
                  <Check className="h-3 w-3 text-primary-foreground" />
                </div>
              </div>
            )}
          </div>

          {/* Contract info */}
          {contracts.length > 0 && (
            <div className="flex items-center gap-1 mt-1">
              <FileText className="h-3 w-3 text-muted-foreground flex-shrink-0" />
              <span className="text-xs">
                {activeContracts.length > 0 ? (
                  <span className="text-warning-on-surface font-medium">
                    {i18n._(msg`${activeContracts.length} active`)}
                  </span>
                ) : (
                  <span className="text-muted-foreground">
                    {i18n._(msg`${contracts.length} contract(s)`)}
                  </span>
                )}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function ChildSelector({
  selectedChildId,
  selectedChildcareId,
  onSelectChild,
  onSelectChildcare,
}: ChildSelectorProps) {
  const { i18n } = useLingui();
  const [searchTerm, setSearchTerm] = useState('');
  // Debounce search to avoid excessive API calls while typing
  const debouncedSearch = useDebouncedSearch(searchTerm, 300);

  // Only show ACTIVE and UPCOMING children in contract wizard
  // Server-side search supports firstname, lastname, and child_internal_id (including leading zeros)
  const { data, isLoading, isFetching } = useChildren(100, 0, {
    status: 'ACTIVE,UPCOMING',
    search: debouncedSearch || undefined,
  });
  const { data: contractsData } = useContracts(undefined, undefined, 100, 0);
  const { data: childcaresData, isLoading: isLoadingChildcares } = useChildcares({ limit: 100, offset: 0 });

  // Data is already filtered server-side
  const children = (data || []) as ChildData[];

  const getChildContracts = (childId: string): ContractData[] => {
    return (contractsData?.contracts?.filter(c => c.childId === childId) || []) as ContractData[];
  };

  const childcares = (childcaresData?.data || []).filter(
    (childcare): childcare is Childcare & { id: string } => Boolean(childcare?.id),
  );
  const selectedChildcare = childcares.find((childcare) => childcare.id === selectedChildcareId);

  const childcareSelectDisabled = isLoadingChildcares || childcares.length === 0;

  React.useEffect(() => {
    if (childcares.length === 1 && !selectedChildcareId) {
      onSelectChildcare(childcares[0].id);
    }
  }, [childcares, selectedChildcareId, onSelectChildcare]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-6">
      <div className="flex min-h-0 flex-1 flex-col gap-6">
        <div>
          <h3 id="child-selector-heading" className="text-lg font-medium">
            {i18n._(msg`Select Child`)}
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            {i18n._(msg`Choose the child for this contract`)}
          </p>
        </div>

        {/* Search */}
        <div className="relative">
          {isFetching && !isLoading ? (
            <Loader2 className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground animate-spin" />
          ) : (
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          )}
          <Input
            placeholder={i18n._(msg`Search by name or ID...`)}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            autoFocus
            aria-label={i18n._(msg`Search for child by name or ID`)}
            aria-describedby="child-selector-heading"
          />
        </div>

        {/* Children List */}
        <div className="flex-1 min-h-0 space-y-1.5 overflow-y-auto pr-1">
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">
              {i18n._(msg`Loading children...`)}
            </div>
          ) : children.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {i18n._(msg`No children found`)}
            </div>
          ) : (
            children.map((child) => {
              const contracts = getChildContracts(child.id);
              const isSelected = selectedChildId === child.id;

              return (
                <ChildCard
                  key={child.id}
                  child={child}
                  isSelected={isSelected}
                  onSelect={() => onSelectChild(child.id)}
                  contracts={contracts}
                  i18n={i18n}
                />
              );
            })
          )}
        </div>
      </div>

      <Card className="mt-auto">
        <CardHeader>
          <CardTitle className="text-base">
            <Trans id="contracts.wizard.fields.childcare">Childcare Location</Trans>
          </CardTitle>
          <CardDescription>
            <Trans id="contracts.wizard.fields.childcareDescription">
              Choose the childcare location where the child will attend
            </Trans>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Select
            value={selectedChildcareId ?? undefined}
            onValueChange={onSelectChildcare}
            disabled={childcareSelectDisabled}
          >
            <SelectTrigger aria-label={i18n._(t`Select childcare location`)}>
              <SelectValue
                placeholder={i18n._(t`Select childcare location`)}
              />
            </SelectTrigger>
            <SelectContent>
              {childcares.map((childcare) => (
                <SelectItem key={childcare.id} value={childcare.id}>
                  {childcare.name}
                  {childcare.canton && ` (${childcare.canton})`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {childcareSelectDisabled && !isLoadingChildcares && (
            <p className="text-sm text-muted-foreground">
              <Trans id="contracts.wizard.childcare.empty">
                No childcare locations available. Configure them in settings.
              </Trans>
            </p>
          )}

          {selectedChildcareId && !selectedChildcare?.canton && (
            <p className="text-sm text-warning-on-surface">
              <Trans id="contracts.wizard.warnings.noCantonConfigured">
                This childcare has no canton configured. Manual date entry will be required.
              </Trans>
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
